/**
 * How the sidebar is grouped. Coleman's layout, 2026-10-07, from the nav
 * board and the review of the first preview:
 *
 *   Home
 *   Community
 *   Learn            Learning Lab, Masterclasses, Insights, X&Os, Resources
 *   Coach            My Players, Training Plans, Playbook, Film, Advantage Flow (web only)
 *   Drill Library    Player Development, Team Coaching, Workouts
 *
 * Learn's heading opens its own page; Drill Library's heading only folds.
 * Movement Drills and Pro are collections, reached from the Collections
 * chips in the library rather than the sidebar. Chats and Calls sit in the
 * sidebar as tiles, not rows (SidebarLive), with My Organization and the
 * Intern Hub as tiles above them for the accounts that have them. My
 * Portal, What's here and Submit Content live in the menu under your name.
 *
 * NAV (navigation.js) is still the list of routes and their own names; this
 * only says where each one sits in the sidebar and, where the sidebar calls
 * it something else, what. The tour's copy of the sidebar and the phone's
 * bottom bar read from here too, so the three cannot drift apart.
 */
import { GraduationCap, ClipboardList, Target } from "lucide-react";

export const HOME_ID = "dashboard";
export const COMMUNITY_ID = "community";
/** In the sidebar as tiles rather than rows. "org" and "interns" are added
 *  per account in BAMFull (a consulting org membership; hubVisible). On
 *  phones they are also in the Coach tab's sheet. */
export const TILE_IDS = ["org", "interns", "chats", "calls"];
/** Reached from the menu under your name. */
export const PROFILE_MENU_IDS = ["portal"];
/** Collections: reached from the chips in the drill library. */
export const COLLECTION_IDS = ["movement", "pro"];

/** [route id, sidebar label (optional, else NAV's)] */
export const SIDEBAR_GROUPS = [
  // `home` is the group's own page: the heading opens it (LearnHome).
  { id: "g-learn", label: "Learn", Icon: GraduationCap, home: "learn-home", items: [
    ["learn"], ["master"], ["insights"], ["xo", "X&Os"], ["resources"],
  ] },
  { id: "g-coach", label: "Coach", Icon: ClipboardList, items: [
    // Consulting is not a place a coach works, so it isn't in the nav: it
    // lives on the Resources page with the other ways to work with BAM
    // (Coleman, 2026-10-10). The #/consulting route still opens.
    // My Organization and the Intern Hub were rows here; they are tiles
    // above Calls and Chats now (Coleman, 2026-10-17). See TILE_IDS.
    // Advantage Flow is its own place, not a corner of Film (Coleman, 2026-10-22). On the web for everyone since
    // 2026-11-03 (a locked preview without it); never in the app.
    ["dev-studio"], ["plans"], ["playbook"], ["film", "Watch Film"], ["afs"],
  ] },
  { id: "g-drills", label: "Drill Library", Icon: Target, items: [
    ["pd", "Player Development"], ["team", "Team Coaching"], ["workouts", "Workouts"],
  ] },
];

/**
 * The groups with their items resolved against a lookup of nav entries.
 * `resolve(id)` returns the entry to show (already filtered and marked for
 * this account) or null to leave it out. A group left with nothing in it is
 * dropped rather than drawn as an empty header.
 */
export function buildGroups(resolve) {
  return SIDEBAR_GROUPS.map(g => ({
    id: g.id, label: g.label, Icon: g.Icon, home: g.home || null,
    items: g.items.map(([id, label]) => {
      const n = resolve(id);
      return n ? { ...n, label: label || n.label } : null;
    }).filter(Boolean),
  })).filter(g => g.items.length > 0);
}

/** Which group a route belongs to, or null. A sub-route counts as its parent,
 *  and a collection counts as the library it is reached from. */
/** Pages that aren't sidebar rows but belong to a group: the Wednesday Drop
 *  sits under Learn, next to the Learning Lab (Coleman, 2026-10-14). */
export const WITHIN_GROUP = { drop: "g-learn", drops: "g-learn" };

export function groupOf(nav) {
  if (!nav || typeof nav !== "string") return null;
  const base = nav.split("/")[0];
  if (COLLECTION_IDS.includes(base)) return "g-drills";
  if (WITHIN_GROUP[base]) return WITHIN_GROUP[base];
  for (const g of SIDEBAR_GROUPS) if (g.home === base || g.items.some(([id]) => id === base)) return g.id;
  return null;
}
